import { Suspense, lazy } from 'react'
import { ClientOnly } from 'remix-utils/client-only'
import type { GetAllPaymentItemsQuery } from 'src/services/apollo/generated/graphql'
import { cn } from 'src/utils/cx'
import css from '../Landing.module.css'
import ExperienceSection from './ExperienceSection'
import FAQSection from './FAQSection'
import FeaturesSection from './FeaturesSection'
import FinalCTA from './FinalCTA'
import GallerySection from './GallerySection'
import { HeroSection } from './HeroSection'
import { Navbar } from './Navbar'
import PricingSection from './PricingSection'
import SocialProofSection from './SocialProofSection'
import StepsSection from './StepsSection'

// Only `GrowthElements` stays lazy + `<ClientOnly>` — it touches
// `document` / `sessionStorage` / scroll listeners at render time and
// renders fixed-position UI (mobile sticky CTA + popup) that has no
// effect on document flow.
//
// `ExperienceSection` *used* to be lazy + `<ClientOnly>` too with the
// reasoning that it is a simulated generation-progress demo and "does
// not benefit from SSR content". The downside of that decision was a
// very visible CLS / flicker on first paint: the section is ~700-800px
// tall, lives in the middle of the page (between Hero and Gallery /
// SocialProof / Steps / Features / Pricing), and during the SSR ↔
// hydration window everything below it shifted by that full height
// once the lazy chunk loaded — what users described as "the page
// flashes". The component itself is fully SSR-safe (only `useState`
// / `useRef`, the loading overlay is gated on a `loading` flag that
// is `false` on the first render), so we render it inline now: the
// SSR HTML reserves the real height up-front and hydration is a
// silent no-op for that subtree.
const LazyGrowthElements = lazy(() => import('./GrowthElements'))

export default function LandingHome({
  initialPaymentItems,
}: {
  /**
   * Forwarded to `<PricingSection>` so server-rendered HTML can include
   * real prices for the three paid tiers when the Landing route has
   * preloaded `paymentItems` via `fetchPaymentItemsForSSR`.
   */
  initialPaymentItems?: GetAllPaymentItemsQuery['paymentItems']
} = {}) {
  return (
    // `css.landing` is required: it owns the `--landing-pink*` /
    // `--landing-bg*` / `--landing-text*` CSS custom properties scoped
    // to this subtree. Tailwind utilities like
    // `bg-[--landing-pink]` / `border-[--landing-pink-light]` /
    // `text-[--landing-pink-dark]` are sprinkled across every section
    // (HeroSection badge, FinalCTA button, Pricing CTAs, etc.); without
    // this class the `var()` references resolve to nothing and the
    // browser falls back to `transparent` — which surfaced as "the
    // button background colors disappeared" reports against the
    // landing page (badge bg, CTA buttons, focus borders all went
    // blank). `css.fadeIn` keeps the existing entrance animation.
    <div className={cn(css.landing, css.fadeIn)}>
      <Navbar />
      <HeroSection />
      <ExperienceSection />
      <GallerySection />
      <SocialProofSection />
      <StepsSection />
      <FeaturesSection />
      <PricingSection initialPaymentItems={initialPaymentItems} />
      <FAQSection />
      <FinalCTA />

      <ClientOnly>
        {() => (
          <Suspense fallback={null}>
            <LazyGrowthElements />
          </Suspense>
        )}
      </ClientOnly>
    </div>
  )
}
